import {
  Button,
  FormFieldGroup,
  Inline,
  Radio,
  Stack,
  Text,
  Textarea,
} from '@amboss/design-system';
import { Field, type FieldProps, Form, Formik } from 'formik';
import { isNull } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { trackEvent } from '../../analytics';
import { config } from '../../config';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import useBottomModal from '../../hooks/useBottomModal';
import useCreditsPaginate from '../../hooks/useCreditsPaginate';
import useLoading from '../../hooks/useLoading';
import { useModal } from '../../hooks/useModal';
import useProfile from '../../hooks/useProfile';
import { useSocketEmit } from '../../hooks/useSocketEmit';
import type { Qa } from '../../interfaces/bottomModal';
import {
  creditsTypeSelector,
  setActiveTab,
  tabSelector,
} from '../../store/credits/creditsSlice';

declare type ClaimModalProps = {
  creditIds: string[];
};

declare type RatingType = {
  qa: Qa[];
  reason?: string;
  note?: string;
};

const claimFormSchema = Yup.object({
  note: Yup.string().required('credits.tabs.tab2.claim.modal.errors.required'),
  qa: Yup.array()
    .of(
      Yup.object({
        question: Yup.number().required(),
        answer: Yup.number()
          .required('credits.tabs.tab2.claim.modal.errors.required')
          .moreThan(-1, 'credits.tabs.tab2.claim.modal.errors.required'),
      }),
    )
    .required(),
  reason: Yup.string().when('qa.3.answer', (value, schema) => {
    return value !== null && value > 0
      ? schema.required('credits.tabs.tab2.claim.modal.errors.required')
      : schema.optional();
  }),
});

type ClaimFormValues = Yup.InferType<typeof claimFormSchema>;

const ClaimModal = ({ creditIds }: ClaimModalProps) => {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const creditType = useAppSelector(creditsTypeSelector);
  const questions: any[] = config.forms.claim.questions.filter(
    (question: any) => !question.hide.includes(creditType),
  );
  const activeTab = useAppSelector(tabSelector);
  const { claimCredit } = useSocketEmit();
  const { closeModal } = useModal();
  const { resetBottomModalForm } = useBottomModal();
  const { setLoading } = useLoading();
  const { refreshCredits } = useCreditsPaginate(config.tabs.REVIEWED);
  const dispatch = useAppDispatch();

  const initialValues: ClaimFormValues = {
    note: '',
    reason: '',
    qa: questions.map((_, index: number) => ({
      question: index,
      // Using -1 as the default value to differentiate between unanswered and answered (0)
      // Not adding a `null` here as it confirms to the Yup schema which expects a number
      answer: -1,
    })),
  };

  const handleSubmit = (values: ClaimFormValues) => {
    const rating: RatingType = { qa: values.qa };

    if (values.reason) {
      rating.reason = values.reason;
    }

    if (values.note) {
      rating.note = values.note;
    }

    setLoading(true);

    claimCredit({ creditIds, rating, type: creditType })
      .then((claimedCredit) => {
        trackSubmittedWithAnswers(rating, claimedCredit);
        trackParsResponse(claimedCredit);
        closeModal();
        resetBottomModalForm();
        refreshCredits().catch((e) => console.log(e, 'refresh'));
        dispatch(setActiveTab(activeTab + 1));
      })
      .catch((e) => {
        console.log(e);
        alert(t('credits.tabs.tab2.claim.error'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const trackSubmittedWithAnswers = (rating: RatingType, claimedCredit: any) => {
    const cmeCreditsClaimSubmittedOptions = {
      xid: profile?.xid,
      cme_activity: config.analytics.activities[creditType],
      question_1: t('credits.tabs.tab2.claim.modal.questions.q1'),
      answer_1: !isNull(rating.qa[0].answer)
        ? t(config.forms.claim.answers[rating.qa[0].answer].label)
        : '',
      question_2: t('credits.tabs.tab2.claim.modal.questions.q2'),
      answer_2: !isNull(rating.qa[1].answer)
        ? t(config.forms.claim.answers[rating.qa[1].answer].label)
        : '',
      question_3: t('credits.tabs.tab2.claim.modal.questions.q3'),
      answer_3: !isNull(rating.qa[2].answer)
        ? t(config.forms.claim.answers[rating.qa[2].answer].label)
        : '',
      question_4: t('credits.tabs.tab2.claim.modal.questions.q4'),
      answer_4: !isNull(rating.qa[3].answer)
        ? t(config.forms.claim.answers[rating.qa[3].answer].label)
        : '',
      reason_4: rating.reason ? rating.reason : '',
      question_5: t('credits.tabs.tab2.claim.modal.note'),
      answer_5: rating.note ? rating.note : '',
      credits_claimed_count: claimedCredit.value,
    };

    trackEvent({
      event: config.analytics.events.CME_CREDITS_CLAIM_SUBMITTED,
      options: cmeCreditsClaimSubmittedOptions,
    });
  };

  const trackParsResponse = (claimedCredit: any) => {
    if (claimedCredit.parsStatus === config.parsStatus.ACCEPTED) {
      trackEvent({
        event: config.analytics.events.CME_CREDITS_CLAIM_SUCCESSFUL,
        options: {
          xid: profile?.xid,
          credits_claimed_count: claimedCredit.value,
          cme_activity: config.analytics.activities[creditType],
        },
      });
    } else {
      const rejectedReason = claimedCredit.parsStatus;

      trackEvent({
        event: config.analytics.events.CME_CREDITS_CLAIM_FAILED,
        options: {
          xid: profile?.xid,
          reason: rejectedReason,
          credits_claimed_count: claimedCredit.value,
        },
      });

      if (claimedCredit.parsStatus === config.parsStatus.REJECTED) {
        const creditRejectedReason = claimedCredit.parsResponse?.errorMessage;
        const creditRejectedCode = claimedCredit.parsResponse?.errorCode;

        trackEvent({
          event: config.analytics.events.CME_CREDITS_CLAIM_REJECTED,
          options: {
            xid: profile?.xid,
            reason: creditRejectedReason,
            error_code: creditRejectedCode,
            status: claimedCredit.parsStatus,
            article_xid: claimedCredit.articleXid,
            cme_activity: config.analytics.activities[creditType],
          },
        });
      }
    }
  };

  return (
    <section className='question-wrapper'>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnChange
        validationSchema={claimFormSchema}
      >
        {({ values, isValid, submitCount }) => {
          const showReasonField = values.qa?.[values.qa.length - 1].answer >= 1;
          const disabled = submitCount > 0 && !isValid;

          return (
            <Form>
              <Stack space='xl'>
                {questions.map((qa, index: number) => (
                  <React.Fragment key={qa.id}>
                    <Field name={`qa.${index}.answer`}>
                      {({ field, form, meta }: FieldProps<string, ClaimFormValues>) => {
                        return (
                          <Stack space={'s'} key={qa.id}>
                            <Text color={'tertiary'}>{t(qa.label)}</Text>
                            <Stack space='s' key={field.name}>
                              <FormFieldGroup
                                label={t(
                                  'credits.tabs.tab2.claim.modal.radio.label',
                                ).toString()}
                                errorMessages={
                                  meta.touched && meta.error ? [t(meta.error)] : undefined
                                }
                              >
                                {config.forms.claim.answers.map(
                                  (answer: any, answerIndex: any) => (
                                    <Radio
                                      key={answerIndex}
                                      label={t(answer.label).toString()}
                                      value={answerIndex}
                                      onChange={(e) => {
                                        form.setFieldValue(
                                          field.name,
                                          Number(e.target.value),
                                        );
                                      }}
                                      name={field.name}
                                    />
                                  ),
                                )}
                              </FormFieldGroup>
                            </Stack>
                          </Stack>
                        );
                      }}
                    </Field>
                  </React.Fragment>
                ))}

                {showReasonField && (
                  <Field name='reason'>
                    {({ field, meta }: FieldProps<string, ClaimFormValues>) => (
                      <Stack space={'s'}>
                        <Text color={'tertiary'}>
                          {t('credits.tabs.tab2.claim.modal.q4reason')}
                        </Text>
                        <Textarea
                          {...field}
                          maxLength={256}
                          hint={t(
                            'credits.tabs.tab2.claim.modal.textarea.hint',
                          ).toString()}
                          hasError={Boolean(meta.touched && meta.error)}
                          errorMessages={
                            meta.touched && meta.error ? [t(meta.error)] : undefined
                          }
                        />
                      </Stack>
                    )}
                  </Field>
                )}

                <Field name='note'>
                  {({ field, meta }: FieldProps<string, ClaimFormValues>) => (
                    <Stack space={'s'}>
                      <Text color={'tertiary'}>
                        {t('credits.tabs.tab2.claim.modal.note')}
                      </Text>
                      <Textarea
                        {...field}
                        maxLength={256}
                        hint={t('credits.tabs.tab2.claim.modal.textarea.hint').toString()}
                        hasError={Boolean(meta.touched && meta.error)}
                        errorMessages={
                          meta.error && meta.touched ? [t(meta.error)] : undefined
                        }
                      />
                    </Stack>
                  )}
                </Field>

                <div
                  style={{
                    position: 'fixed',
                    bottom: 24,
                    right: 32,
                    backgroundColor: '#fff',
                    borderRadius: 4,
                    zIndex: 9999,
                  }}
                >
                  <Inline alignItems={'right'} space={'s'}>
                    <Button type='submit' disabled={disabled}>
                      {t('credits.modal.submit')}
                    </Button>
                  </Inline>
                </div>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </section>
  );
};

export default ClaimModal;
